import $ from 'jquery';
import React, {UpdateSpec} from 'react';
import _ from 'underscore';

import mediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {EditorProps, StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import ImageEditable from 'notebook/protocols/editor/ImageEditable';
import {
  DescriptionComponent,
  HeaderComponent,
  StepComponent,
  TimerComponent,
} from 'notebook/protocols/editor/StepsEditorComponents';
import * as StepConstants from 'notebook/protocols/StepConstants';
import {Step, StepLevel} from 'notebook/protocols/StepConstants';
import {Image} from 'notebook/protocols/types/ImageType';
import AggregateTimeComponentClass from 'notebook/protocols/viewer/AggregateTimeComponent';
import {Table} from 'notebook/tables/constants/TableConstants';
import makeTableHTML from 'notebook/tables/export/makeTableHTML';
import TableUtil from 'notebook/tables/TableUtil';

import {StepsEditorPlugin, StepsEditorPluginComponent} from './StepsEditorTypes';

function typeTagHTML(step: Step): JQuery {
  const $span = $('<span></span>');
  switch (step.type) {
    case StepConstants.TYPE_CRITICAL:
      $span.append($('<b>[CRITICAL] </b>').css('color', '#3498db'));
      break;
    case StepConstants.TYPE_PAUSE:
      $span.append($('<b>[PAUSE] </b>').css('color', '#2ecc71'));
      break;
    default:
    // don't append anything
  }
  return $span;
}

const basePlugin = {
  itemFromHTML($el: JQuery, item: Step): Step {
    return item;
  },
  getContent({text, formats}: {text: string; formats: Format}): {text: string; formats: Format} {
    return {text, formats};
  },
  setContent({text, formats}: {text: string; formats: Format}): UpdateSpec {
    return {
      $merge: {text, formats},
    };
  },
  getText({text, formats}: {text: string | null; formats: Format}): {text: string; formats: Format} {
    return {text: text ?? '', formats};
  },
  setText({text, formats}: {text: string; formats: Format}): UpdateSpec {
    return {
      $merge: {text, formats},
    };
  },
};

const IMAGE_PLUGIN_COMPONENT: StepsEditorPluginComponent & {title: string} = {
  ...mediocrePlugins.blockPluginBase,
  title: 'Image', // TODO: Remove - unused
  default(): {} {
    return {};
  },
  required: ['itemUpdateHandler', 'itemDeleteHandler', 'itemMouseDownHandler', 'itemMouseOverHandler'],
  make(item: Step, index: number, editorProps: EditorProps<Step>, itemProps: undefined): JSX.Element {
    // Note: Do not directly access @props or @state or indirectly through a method call here (D5845)
    const cid = editorProps.getCid(item);
    const content = editorProps.getContent(item) as Image;
    return (
      <ImageEditable
        cid={cid}
        image={content}
        pasteHandler={this.pasteHandler.bind(this, index)}
        updateHandler={this.itemUpdateHandler.bind(this, index)}
        deleteHandler={this.itemDeleteHandler.bind(this, index)}
        mouseDownHandler={this.itemMouseDownHandler.bind(this, index)}
        mouseOverHandler={this.itemMouseOverHandler.bind(this, index)}
        locked={editorProps.locked}
        owningEntityApiId={this.props.fileId}
      />
    );
  },
};

const STEPS_EDITOR_PLUGINS: Array<StepsEditorPlugin> = [
  _.defaults(
    {
      type: StepLevel.HEADER,
      typeClass: 'is-header',
      title: 'Header',
      icon: 'protocol-header',
      canPasteFromOtherTenant: true,
      component: HeaderComponent,
      textual: true,
      itemFromHTML($el: JQuery, item: Step): Step {
        return item;
      },
      itemToHTML($el: JQuery, item: Step): JQuery {
        const $h1 = $('<h1></h1>')
          .html(formatters.renderHTML(item.text ?? '', item.formats, {forceNonEmpty: true}))
          .prepend(typeTagHTML(item));
        if (item.time_seconds != null) {
          $h1.append($(`<em> (${AggregateTimeComponentClass.formattedTime(item.time_seconds)})</em>`));
        }
        $el.append($h1);
        return $el;
      },
    },
    basePlugin
  ),

  _.defaults(
    {
      type: StepLevel.STEP,
      typeClass: 'is-step',
      title: 'Step',
      icon: 'protocol-step',
      canPasteFromOtherTenant: true,
      component: StepComponent,
      textual: true,
      itemFromHTML($el: JQuery, item: Step): Step {
        return item;
      },
      itemToHTML($el: JQuery, item: Step): JQuery {
        const $p = $('<p></p>')
          .html(formatters.renderHTML(item.text ?? '', item.formats, {forceNonEmpty: true}))
          .prepend(typeTagHTML(item));
        if (item.time_seconds != null) {
          $p.append($(`<em> (${AggregateTimeComponentClass.formattedTime(item.time_seconds)})</em>`));
        }
        $el.append($p);
        return $el;
      },
      setContent({
        text,
        formats,
        completed,
      }: {
        text: string;
        formats?: Format;
        completed: boolean;
      }): UpdateSpec {
        return {$merge: {text, formats, completed}};
      },
      getContent({text, formats, completed}: {text: string; formats?: Format; completed: boolean}): {
        text: string;
        formats: Format | undefined;
        completed: boolean;
      } {
        return {text, formats, completed};
      },
    },
    basePlugin
  ),

  _.defaults(
    {
      type: StepLevel.DESC,
      typeClass: 'is-description',
      title: 'Description',
      icon: 'protocol-desc',
      canPasteFromOtherTenant: true,
      component: DescriptionComponent,
      textual: true,
      itemFromHTML($el: JQuery, item: Step): Step {
        return item;
      },
      itemToHTML($el: JQuery, item: Step): JQuery {
        const $p = $('<p></p>')
          .html(formatters.renderHTML(item.text ?? '', item.formats, {forceNonEmpty: true}))
          .prepend(typeTagHTML(item));
        $el.append($p);
        return $el;
      },
    },
    basePlugin
  ),

  _.defaults(
    {
      type: StepLevel.TABLE,
      typeClass: 'is-table',
      title: 'Table',
      icon: 'protocol-table',
      canPasteFromOtherTenant: true,
      component: mediocrePlugins.table,
      textual: false,
      setContent({table}: {table: Table}): UpdateSpec {
        return {$merge: {table}};
      },
      getContent({table}: {table: Table}): {table: Table} {
        return {table};
      },
      getSpreadsheetTableName({table}: {table: Table}): string {
        return table.name;
      },
      itemFromHTML($el: JQuery, item: Step): Step {
        return item;
      },
      itemToHTML($el: JQuery, item: Step): JQuery {
        const [height, width] = TableUtil.getDimensions(item.table!);
        $el.append(
          makeTableHTML(item.table!, this.props.computedTables[item.table!.name], {
            minI: 0,
            minJ: 0,
            maxI: height - 1,
            maxJ: width - 1,
          })
        );
        return $el;
      },
      getStoreDependenciesForSpreadsheetEval(item: Step): StoreDependencyMap {
        return {};
      },
    },
    basePlugin
  ),

  _.defaults(
    {
      type: StepLevel.IMAGE,
      typeClass: 'is-image',
      title: 'Image',
      icon: 'protocol-image',
      canPasteFromOtherTenant: false,
      component: IMAGE_PLUGIN_COMPONENT,
      textual: false,
      itemToHTML($el: JQuery, item: Step): JQuery {
        $el.append($('<img>').attr('src', item.image!.url));
        return $el;
      },
      setContent(image: {url: string}): UpdateSpec {
        return {$merge: {image}};
      },
      getContent({image}: {image: {url: string}}): {url: string} {
        return image;
      },
    },
    basePlugin
  ),

  _.defaults(
    {
      type: StepLevel.TIMER,
      typeClass: 'is-timer',
      title: 'Timer',
      icon: 'protocol-timer',
      canPasteFromOtherTenant: true,
      component: TimerComponent,
      itemToHTML($el: JQuery, item: Step): JQuery {
        const timeSeconds = item.timer!.milliseconds / 1000;
        $el
          .append($('<b>').text('Timer'))
          .append($('<span>: </span>'))
          .append($('<span>').text(AggregateTimeComponentClass.formattedTime(timeSeconds)));
        return $el;
      },
      setContent(time: number): UpdateSpec {
        return {
          $merge: {
            timer: {milliseconds: time * 1000},
          },
        };
      },
      getContent({timer}: {timer: {milliseconds: number}}): {milliseconds: number} {
        return timer;
      },
    },
    basePlugin
  ),
];

for (const plugin of STEPS_EDITOR_PLUGINS) {
  if (!('canPasteFromOtherTenant' in plugin)) {
    throw new Error('Steps editor plugins must define canPasteFromOtherTenant');
  }
}

export default STEPS_EDITOR_PLUGINS;
